import * as React from 'react';
import { PolymorphicRef } from '../../../utils/PolymorphicTypes';
import { LinkBaseProps, LinkBaseComponent } from './LinkBaseTypes';
import { disabledColor } from './LinkStyleObjects';
import { useLinkTheme } from '../../../themes';
import { Typography } from '../../index';
import { LinkInputValidationError } from './LinkErrors';

export const LinkBase: LinkBaseComponent = React.forwardRef(
    <C extends React.ElementType = 'a'>({
    as,
    children,
    color,
    colorStyle='default',
    decorator='always',
    disabled=false,
    display,
    href,
    id,
    noRoute=false,
    onClick,
    overrideHoverColor=false,
    target,
    typographyProps,
    typographyType,
    variant,
    ...htmlProps
}:LinkBaseProps<C>, 
ref?:PolymorphicRef<C>) => {

    /* @start Input validation */
    if (!children) {
        throw new LinkInputValidationError('`children` prop cannot be empty');
        }
    /* @end Input validation */

    const Component = (disabled || noRoute) && 
        (!as || (typeof as === 'string' && as === 'a') || (typeof as === 'object')) 
            ? 'span' : (as || 'a');

    const { linkTheme } = useLinkTheme();
    const linkClasses = [
        linkTheme[`bsw-link`],
        variant ? linkTheme[`bsw-link--link-${variant}`] : undefined,
        disabled ? linkTheme['bsw-link--disabled'] : undefined,
        noRoute ? linkTheme['bsw-link--no-route'] : undefined,
        overrideHoverColor  ? linkTheme['bsw-link--override-hover-color'] : undefined,
        htmlProps.className
    ].join(' ');

    const _color = disabled ? disabledColor : typographyProps?.color ? typographyProps?.color : color;
    const _typographyType = typographyProps?.type ? typographyProps?.type : typographyType;
    const _style = { 
        // Need to set color on hyperlink too in case of color conflicts with existing site global hyperlink colors or when nested within another Typography component 
        color: _color ? linkTheme[`${_color.replace('$', '')}`] : _typographyType && (_typographyType).indexOf('body-') > -1 ? linkTheme['typography-color-secondary'] : linkTheme['typography-color-primary'],
        display: `${display}`,
        // Need to set text decoration none since underline is on nested Typography not directly on hyperlink
        textDecoration: 'none'
    };

    const linkContent = <Typography
        {...typographyProps}
        type={_typographyType}
        color={_color}
    >
        {children}
    </Typography>;

    // Include disabled for elements that it is a valid attribute
    const _showDisabledAttr=(as && typeof as === 'string' && (as === 'button' || as === 'input')) ? disabled : undefined;

    const defaultProps = {
        ...htmlProps,
        className:linkClasses,
        disabled:_showDisabledAttr,
        href:href,
        id:id,
        onClick:onClick,
        ref:ref,
        style:_style,
        target:target
    }

    const disabledProps = {
        className:linkClasses,
        disabled:_showDisabledAttr,
        id:id,
        ref:ref,
        style:_style
    }

    const componentProps = (disabled || noRoute) ? disabledProps : defaultProps;

    return (
        <Component
        {...componentProps}
    >
        {linkContent}
    </Component>
    );
});